import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/html-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import { BaseModuleProps, HTMLModuleSettings } from "../../types/index.js";
import { getActiveColorScheme, isDefined } from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<HTMLModuleSettings>;

const HTMLModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { textAlign },
    translation: {
      settings: { title, subtitle, html },
    },
  },
  translatedModule,
  isFirstOnPage,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const [isLazyloaded, setIsLazyloaded] = useState(false);
  const scriptURLsRef = useRef<string[]>([]);
  const insertedScriptsRef = useRef<HTMLScriptElement[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const htmlSetRef = useRef(false);

  useEffect(() => {
    if (!html || !isLazyloaded || !contentRef.current || htmlSetRef.current) {
      return;
    }

    const extracted = extractHTMLAndScriptURLs(html);
    scriptURLsRef.current = extracted.scriptURLs;

    // Manually set innerHTML instead of using React’s dangerouslySetInnerHTML.
    // This prevents React from resetting the HTML which could break the HTML
    // code, e.g. widget scripts relying on the HTML to be present continuously.
    contentRef.current.innerHTML = extracted.html;
    htmlSetRef.current = true;

    insertedScriptsRef.current = scriptURLsRef.current.map(insertScript);
  }, [html, isLazyloaded]);

  useEffect(() => {
    return () => {
      // Clean up all inserted scripts.
      insertedScriptsRef.current.forEach((el) => el.remove());
      insertedScriptsRef.current = [];
      scriptURLsRef.current.forEach(URL.revokeObjectURL);
      htmlSetRef.current = false;
    };
  }, [html]);

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.HTMLModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>
      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          ref={contentRef}
          className={ClassNames(moduleBaseStyles.Module__Wrapper, {
            [styles["HTMLModule__Content--empty"]]: !html,
          })}
        />
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

/**
 * Process the HTML of this module with DOM APIs:
 * - Extract script URLs (for both external JS files and inline scripts).
 * - Remove the script tags from the resulting HTML.
 *
 * Remember to revoke the resulting object URLs.
 */
const extractHTMLAndScriptURLs = (
  html: string,
): { html: string; scriptURLs: string[] } => {
  const div = document.createElement("div");
  div.innerHTML = html.trim();

  const scriptElements = Array.from(div.querySelectorAll("script"));

  const urls = scriptElements
    .map(({ src, innerText }) => {
      switch (true) {
        case Boolean(src):
          return src;
        case Boolean(innerText):
          return URL.createObjectURL(
            new Blob([innerText], { type: "text/javascript" }),
          );
        default:
          return undefined;
      }
    })
    .filter(isDefined);

  for (const el of scriptElements) {
    el.remove();
  }

  return { html: div.innerHTML, scriptURLs: urls };
};

const insertScript = (url: string): HTMLScriptElement => {
  const script = document.createElement("script");
  script.src = url;
  script.defer = true;
  document.head.appendChild(script);
  return script;
};

export default HTMLModule;
